import { useContext, useEffect, useRef } from "react";
import styled from "styled-components";
import { hideScrollBar } from "../../../../../../commonStyles";
import { MAX_MODAL_HEIGHT } from "../../../../../../utils";
import { PostCommentField } from "../../../../../Common";
import { ModalContext } from "../../../../../Common/Modal";
import { FlexColumn, FlexRow } from "../../../../../Common/Styles";
import { Header } from "../1-Header";
import { Topics } from "../2-Topics";
import { Footer } from "../4-Footer";
import { usePostCardContext } from "../PostCardContent";
import { AudioContent } from "./AudioContent";
import { GIFContent } from "./GIFContent";
import { ImageContent } from "./ImageContent";
import { PostPollContent } from "./PostPollContent";
import { PostRateContent } from "./PostRateContent";
import { TextContent } from "./TextContent";
import { VideoContent } from "./VideoContent";

export const ExpandedContentLayout = () => {
  const { maxContentHeight } = useContext(ModalContext);
  const { postId, isOnExpandedPostView } = usePostCardContext();

  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOnExpandedPostView) return;
    if (!rightRef.current || !leftRef.current) return;
    leftRef.current.scrollIntoView({ behavior: "auto", block: "center" });
    rightRef.current.scrollIntoView({ behavior: "auto", block: "center" });
  }, [isOnExpandedPostView]);

  return (
    <Container className="expanded-layout-container" $gapInPx={0}>
      <LeftContainer className="left-container" $maxHeight={maxContentHeight}>
        <div ref={leftRef}>
          <ImageContent />
          <PostPollContent />
          <PostRateContent />

          <AudioContent />

          <VideoContent />

          <GIFContent />
        </div>
      </LeftContainer>
      <RightContainer
        $maxHeight={maxContentHeight}
        id="expanded-post-card-right-container"
      >
        <div ref={rightRef}>
          <Header />
          <Topics />
          <TextContentContainer>
            <TextContent />
          </TextContentContainer>
          <Footer />
        </div>
        <StickyFooter>
          <PostCommentField
            parent_comment_id={null}
            post_id={postId}
            scrollAreaId=""
          />
        </StickyFooter>
      </RightContainer>
    </Container>
  );
};

const Container = styled(FlexRow)`
  height: 100%;
  flex-grow: 1;
  align-items: center;
  background: linear-gradient(
    to right,
    ${({ theme }) => theme.Color.elevation.surface["surface-primary"].value},
    rgba(30, 150, 242, 0.08)
  );
`;

const ColumnContainer = styled(FlexColumn)<{ $maxHeight: number }>`
  overflow-y: scroll;
  ${hideScrollBar};
  max-height: ${({ $maxHeight }) => $maxHeight - 2}px;
  padding: 0;
  height: 100%;
`;
const LeftContainer = styled(ColumnContainer)`
  min-width: 377px;
  padding: ${({ theme }) => theme.spacing["spacing-2"].value}rem
    ${({ theme }) => theme.spacing["spacing-none"].value}rem;
  & > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
  }
`;
const RightContainer = styled(ColumnContainer)`
  flex-grow: 1;
  min-width: 377px;
  max-width: 377px;
  border-left: 1px solid
    ${({ theme }) => theme.Color.border["ui-border-subtle"].value};
  background: ${({ theme }) =>
    theme.Color.elevation.surface["surface-overlay"].value};
  box-shadow: 0px 4px 20px 0px #cdcdcd29;
  justify-content: space-between;
  height: ${MAX_MODAL_HEIGHT}dvh;
`;

const TextContentContainer = styled.div`
  padding: 0 ${({ theme }) => theme.spacing["spacing-2"].value}rem;
`;
const StickyFooter = styled.div`
  background: ${({ theme }) =>
    theme.Color.elevation.surface["surface-overlay"].value};
  position: sticky;
  bottom: 0;
  border-top: ${({ theme }) => theme.border_width["border-width-thin"].value}rem
    solid ${({ theme }) => theme.Color.border["ui-border-neutral"].value};
  padding: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
`;
